import { toast } from "sonner";

import { workspace } from "@/components/workspace/controller";
import { keys } from "@/hooks/queries";
import {
  api,
  queryClient,
  type CopyResult,
  type FolderItemOut,
  type NoteCategory,
} from "@/lib/api";
import { useShell } from "@/stores/shell";

import { useFileSelection, type FileRef } from "./file-selection";

/**
 * Open a file. The shared routing rule for the folder surfaces, so a note opens
 * the same way wherever it was clicked. Previously the rail and the compact
 * file panel each carried their own copy of this, kept in sync by hand and a
 * comment saying so.
 *
 * It is NOT yet the only router. Three surfaces still route on their own,
 * because each needs something this signature does not take: `home/NoteCard`
 * (a `background` flag for ⌘-click, plus the failed-ingest toast),
 * `files/FileTreePanel` and `workspace/content/ExplorerSidebar` (both take a
 * vault `ref` and fetch the node label before opening the markdown editor).
 */
export function openFile(item: Pick<FolderItemOut, "id" | "kind" | "title">): void {
  useShell.getState().closeAll();
  if (item.kind === "node") {
    workspace.openMarkdown(item.id, item.title);
    return;
  }
  workspace.openDoc({ id: item.id, title: item.title, docKind: "document" });
}

/** Rename a file in place. A document's title IS its display name (Obsidian
 * style) and a note's label is its filename, so the two take different routes to
 * the same user-visible outcome. */
export async function renameFile(ref: FileRef, title: string): Promise<void> {
  const next = title.trim();
  if (!next || next === ref.title) return;
  try {
    if (ref.kind === "document") {
      await api.request("PATCH", `/api/documents/${ref.id}`, { title: next });
    } else {
      await api.request("PATCH", `/api/nodes/${ref.id}`, { label: next });
    }
    await invalidateFiles();
    toast.success(`Renamed to “${next}”`);
  } catch (error) {
    toast.error("Couldn't rename", { description: String(error) });
  }
}

/** Delete files. Deliberately has no confirm of its own: the surface that calls
 * it owns the confirmation, because an inline row and a context menu want very
 * different affordances, and a function that both confirms and deletes cannot be
 * reused by either. */
export async function deleteFiles(refs: FileRef[]): Promise<void> {
  let removed = 0;
  const failures: string[] = [];
  for (const ref of refs) {
    try {
      // Deleting a DOCUMENT removes the whole document (so it can be
      // re-uploaded); a note is just a node. Same rule the inspector uses.
      const path = ref.kind === "document" ? `/api/documents/${ref.id}` : `/api/nodes/${ref.id}`;
      await api.request("DELETE", path);
      removed += 1;
    } catch (error) {
      failures.push(`${ref.title}: ${String(error)}`);
    }
  }
  useFileSelection.getState().clear();
  await invalidateFiles();
  if (failures.length) {
    toast.error(`Couldn't delete ${failures.length} of ${refs.length}`, {
      description: failures[0],
    });
  } else if (removed) {
    toast.success(removed === 1 ? "Deleted" : `Deleted ${removed} files`);
  }
}

/** Create (or adopt) a folder. Idempotent server-side, so this doubles as
 * "give this derived folder an identity" — which is what colouring a folder
 * nobody has ever touched has to do first. Passing the colour here rather than
 * in a follow-up PATCH keeps that a single round trip, and means the colour can
 * never be lost between the two calls. */
/** Duplicate files. The server names the copy with the vault's own collision
 * scheme (`Notes` -> `Notes (2)`), so the file on disk and the note in the app
 * agree about what the duplicate is called. */
export async function copyFiles(refs: FileRef[], folder?: string): Promise<void> {
  if (!refs.length) return;
  try {
    const result = await api.request<CopyResult>("POST", "/api/vault/copy", {
      items: refs.map((r) => ({ kind: r.kind, id: r.id })),
      // Undefined means "beside the original" (Duplicate); a string — including
      // "" for a category root — means "paste it HERE".
      ...(folder === undefined ? {} : { folder }),
    });
    await invalidateFiles();
    if (result.rejected.length && !result.created.length) {
      toast.error("Couldn't duplicate", { description: result.rejected[0]?.reason });
    } else if (result.rejected.length) {
      toast.warning(`Duplicated ${result.created.length}, skipped ${result.rejected.length}`, {
        description: result.rejected[0]?.reason,
      });
    } else {
      toast.success(
        result.created.length === 1 ? "Duplicated" : `Duplicated ${result.created.length} files`,
      );
    }
  } catch (error) {
    toast.error("Couldn't duplicate", { description: String(error) });
  }
}

export async function createFolder(
  category: NoteCategory | null,
  path: string,
  color?: string | null,
): Promise<boolean> {
  try {
    await api.request("POST", "/api/folders", { category, path, color: color ?? null });
    await invalidateFiles();
    return true;
  } catch (error) {
    toast.error("Couldn't create the folder", { description: String(error) });
    return false;
  }
}

export async function setFolderColor(folderId: string, color: string | null): Promise<void> {
  try {
    await api.request("PATCH", `/api/folders/${folderId}`, { color: color ?? "" });
    await invalidateFiles();
  } catch (error) {
    toast.error("Couldn't set the colour", { description: String(error) });
  }
}

/** Rename or move a folder. `path` is the folder's FULL new path within its
 * category, so one call expresses both. */
export async function renameFolder(folderId: string, path: string): Promise<void> {
  try {
    await api.request("PATCH", `/api/folders/${folderId}`, { path });
    await invalidateFiles();
    toast.success("Folder renamed");
  } catch (error) {
    toast.error("Couldn't rename the folder", { description: String(error) });
  }
}

/** Delete a folder. The server NEVER deletes the notes inside — they move up to
 * the parent — and says how many did, which is what the toast reports. */
export async function deleteFolder(folderId: string): Promise<void> {
  try {
    // FolderDeleteResult also carries `deleted` and `moved_folders` (the
    // subfolder count the route docstring promises); neither is read here yet.
    const result = await api.request<{
      deleted: boolean;
      moved_items: number;
      moved_folders: number;
      parent_path: string;
    }>("DELETE", `/api/folders/${folderId}`);
    await invalidateFiles();
    toast.success(
      result.moved_items
        ? `Folder deleted · ${result.moved_items} moved to ${result.parent_path || "the top level"}`
        : "Folder deleted",
    );
  } catch (error) {
    toast.error("Couldn't delete the folder", { description: String(error) });
  }
}

/** Every cache a filesystem change invalidates, in one place — the explorer
 * tree, the homepage feed, and the legacy disk-mirroring tree. Forgetting one of
 * these is how a surface ends up showing a file that has already moved. */
export async function invalidateFiles(): Promise<void> {
  await Promise.all([
    // `keys.vault` is the shared PREFIX for every filesystem view (see
    // hooks/queries.ts) — one call covers the folder tree, the explorer's
    // tree-with-files, and the legacy disk-mirroring tree.
    queryClient.invalidateQueries({ queryKey: keys.vault }),
    queryClient.invalidateQueries({ queryKey: ["notes"] }),
    queryClient.invalidateQueries({ queryKey: keys.overview }),
  ]);
}
